import React, { useState, useEffect } from 'react';
import { fixPriceNumbers } from '@/utils/planFeatures';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StyledText } from '@/hooks/useStyledTranslation';
import T from '@/components/StyledTranslation';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { BookOpen, FileQuestion, PlayCircle, MessageCircle, ArrowRight, CheckCircle, Shield, Award, Users, Clock, Gauge, Target, Trophy, Zap, Star, AlertTriangle, Globe, UserPlus, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import PricingCard from '@/components/PricingCard';
import { sortPlans, monthlyBase, monthsLabel } from '@/utils/planPricing';
import SEO from '@/components/SEO';
import { useLocalizedNavigation } from '@/hooks/useLocalizedNavigation';
import { switchLanguagePath } from '@/utils/localizedRoutes';
import { LandingPageSchemas } from '@/components/StructuredData';
import { ReviewsSection } from '@/components/Reviews';
import { API } from '@/config';

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { getLink, routes } = useLocalizedNavigation();
  const isRTL = i18n.language === 'ar';
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  
  // Default stats - shown immediately
  const defaultStats = [
    { value: '800+', label: { en: 'B-license Q', ar: 'سؤال B', sv: 'B-frågor', tr: 'B-soru', fa: 'سؤال B', so: 'Su\'aalo B', pl: 'Pytań B', es: 'Preguntas B', de: 'B-Fragen' } },
    { value: '480+', label: { en: 'Taxi questions', ar: 'سؤال تاكسي', sv: 'Taxi-frågor', tr: 'Taksi soru', fa: 'سؤال تاکسی', so: 'Su\'aalo Taxi', pl: 'Pytań Taxi', es: 'Preguntas Taxi', de: 'Taxi-Fragen' } },
    { value: '26', label: { en: 'Lessons', ar: 'درس', sv: 'Lektioner', tr: 'Ders', fa: 'درس', so: 'Casharo', pl: 'Lekcji', es: 'Lecciones', de: 'Lektionen' } },
    { value: '9', label: { en: 'Languages', ar: 'لغات', sv: 'Språk', tr: 'Dil', fa: 'زبان', so: 'Luqado', pl: 'Języków', es: 'Idiomas', de: 'Sprachen' } },
  ];
  
  const [stats, setStats] = useState(defaultStats);
  const [metrics, setMetrics] = useState([]);
  
  // Default features - shown immediately, updated if API returns data
  const defaultFeatures = [
    { icon: BookOpen, title: { en: 'Theory Lessons', ar: 'دروس نظرية', sv: 'Teorilektioner', tr: 'Teori Dersleri', fa: 'دروس تئوری', so: 'Casharada', pl: 'Lekcje teorii', es: 'Lecciones de teoría', de: 'Theorie-Lektionen' }, description: { en: 'Comprehensive driving theory lessons', ar: 'دروس نظرية شاملة للقيادة', sv: 'Omfattande körteorilektioner', tr: 'Kapsamlı sürüş teorisi dersleri', fa: 'دروس جامع تئوری رانندگی', so: 'Casharada aragtida wadista', pl: 'Kompleksowe lekcje teorii jazdy', es: 'Lecciones completas de teoría de conducción', de: 'Umfassende Fahrtheorie-Lektionen' }, link: '/teorilektioner' },
    { icon: FileQuestion, title: { en: 'Practice Tests', ar: 'اختبارات تدريبية', sv: 'Övningsprov', tr: 'Pratik Testler', fa: 'آزمون‌های تمرینی', so: 'Imtixaanada', pl: 'Testy praktyczne', es: 'Tests de práctica', de: 'Übungstests' }, description: { en: 'Test your knowledge with practice exams', ar: 'اختبر معلوماتك مع الامتحانات التدريبية', sv: 'Testa dina kunskaper med övningsprov', tr: 'Pratik sınavlarla bilginizi test edin', fa: 'دانش خود را با آزمون‌های تمرینی بسنجید', so: 'Tijaabi aqoontaada imtixaanada', pl: 'Sprawdź swoją wiedzę z testami praktycznymi', es: 'Prueba tus conocimientos con exámenes de práctica', de: 'Testen Sie Ihr Wissen mit Übungsprüfungen' }, link: '/teoriprov' },
    { icon: AlertTriangle, title: { en: 'Traffic Signs', ar: 'إشارات المرور', sv: 'Trafikmärken', tr: 'Trafik İşaretleri', fa: 'علائم راهنمایی', so: 'Calaamadaha Taraafikada', pl: 'Znaki drogowe', es: 'Señales de tráfico', de: 'Verkehrszeichen' }, description: { en: 'Learn all traffic signs and their meanings', ar: 'تعلم جميع إشارات المرور ومعانيها', sv: 'Lär dig alla trafikmärken och deras betydelser', tr: 'Tüm trafik işaretlerini ve anlamlarını öğrenin', fa: 'همه علائم راهنمایی و معانی آنها را بیاموزید', so: 'Baro dhammaan calaamadaha taraafikada', pl: 'Poznaj wszystkie znaki drogowe i ich znaczenia', es: 'Aprende todas las señales de tráfico y sus significados', de: 'Lernen Sie alle Verkehrszeichen und ihre Bedeutungen' }, link: '/trafikmarken' },
    { icon: MessageCircle, title: { en: 'AI Assistant', ar: 'المساعد الذكي', sv: 'AI-Assistent', tr: 'AI Asistan', fa: 'دستیار هوشمند', so: 'Kaaliyaha AI', pl: 'Asystent AI', es: 'Asistente IA', de: 'KI-Assistent' }, description: { en: 'Get help from our AI tutor', ar: 'احصل على مساعدة من معلمنا الذكي', sv: 'Få hjälp från vår AI-lärare', tr: 'AI öğretmenimizden yardım alın', fa: 'از معلم هوش مصنوعی ما کمک بگیرید', so: 'Hel caawimaad macalinka AI', pl: 'Uzyskaj pomoc od naszego nauczyciela AI', es: 'Obtén ayuda de nuestro tutor de IA', de: 'Holen Sie sich Hilfe von unserem KI-Tutor' }, link: '/ai-assistent' },
  ];
  
  const [features, setFeatures] = useState(defaultFeatures);
  const [loading, setLoading] = useState(false);
  // TEMP: Visual-only toggle to preview B-körkort / Taxikort split on the
  // landing pricing section. Mirrors the mockup on /priser. No backend wiring.
  const [licenseTab, setLicenseTab] = useState('b_korkort');

  // Icon mapping for dynamic data
  const iconMap = {
    Users, BookOpen, FileQuestion, PlayCircle, MessageCircle, Award, Clock, 
    Gauge, Trophy, Shield, Target, Zap, Star, CheckCircle, AlertTriangle
  };

  const pricingFeatures = t('pricing.features', { returnObjects: true });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      // Fetch all data in parallel
      const [plansRes, statsRes, metricsRes, featuresRes] = await Promise.all([
        axios.get(`${API}/subscription-plans/public`),
        axios.get(`${API}/site/statistics`),
        axios.get(`${API}/site/metrics`),
        axios.get(`${API}/site/features`)
      ]);

      setPlans(sortPlans((plansRes.data || []).filter((p) => p.isActive !== false && p.price > 0)).slice(0, 3));
      
      // Only update stats if API returns data - merge with translations
      const statsData = statsRes.data || [];
      if (statsData.length > 0) {
        // Merge API data with our translations - prefer API translations if available
        const mergedStats = statsData.map(apiStat => {
          // Find matching default stat by English label
          const defaultStat = defaultStats.find(d => 
            d.label?.en?.toLowerCase() === apiStat.label?.en?.toLowerCase() ||
            d.label?.sv?.toLowerCase() === apiStat.label?.sv?.toLowerCase()
          );
          
          if (defaultStat) {
            // Merge: use API value, but keep all our translations
            return {
              ...apiStat,
              label: { ...defaultStat.label, ...apiStat.label }
            };
          }
          return apiStat;
        });
        setStats(mergedStats);
      }
      
      setMetrics(metricsRes.data || []);
      
      // Map features with icons - only update if API returns data
      const featuresData = featuresRes.data || [];
      if (featuresData.length > 0) {
        const mappedFeatures = featuresData.map(f => ({
          ...f,
          icon: iconMap[f.icon] || BookOpen
        }));
        setFeatures(mappedFeatures);
      }
      // If empty, keep the default features already set
    } catch (error) {
      console.error('Error fetching data:', error);
      // Keep default features on error - they're already set
    } finally {
      setLoadingPlans(false);
    }
  };

  // دالة للاشتراك - تحفظ الخطة المختارة ثم توجه للتسجيل أو الدفع
  const handleSubscribe = async (plan) => {
    if (isAuthenticated) {
      // إذا كان مسجل الدخول بالفعل، توجيه مباشرة للدفع
      if (plan.price > 0) {
        // خطة مدفوعة - توجيه مباشر لـ Stripe Checkout
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API}/checkout/session?plan_id=${plan.id}&licenseType=${licenseTab}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.url) {
            window.location.href = response.data.url;
          }
        } catch (error) {
          console.error('Error creating checkout session:', error);
          // في حالة الخطأ، توجيه لصفحة الأسعار
          navigate(routes.pricing);
        }
      } else {
        // خطة مجانية - توجيه للاختبارات
        navigate(routes.tests);
      }
      return;
    }

    // حفظ الخطة المختارة في localStorage للتوجيه بعد التسجيل
    localStorage.setItem('pendingPlanId', plan.id);
    localStorage.setItem('pendingPlanPrice', plan.price.toString());
    // Persist which license (B or Taxi) the guest picked so post-signup
    // auto-checkout activates the correct subscription.
    localStorage.setItem('pendingPlanLicense', licenseTab);
    navigate(routes.register);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-slate-950" data-testid="landing-page">
      <SEO page="home" />
      <LandingPageSchemas language={i18n.language} />
      <Navbar />
      
      {/* Hero Section - Professional Clean Design */}
      <section className="relative pt-16 pb-6 sm:pb-12 overflow-hidden bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 lg:gap-16 items-center py-2 sm:py-8 lg:py-12">
            
            {/* صورة الهيرو للموبايل */}
            <div className="lg:hidden rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/images/hero-mobile.webp" 
                alt="Driving education" 
                fetchPriority="high"
                width="600"
                height="400"
                className="w-full h-44 sm:h-56 object-cover"
              />
            </div>

            {/* Desktop Image - Left Side */}
            <div 
              className={`hidden lg:block animate-fade-in-left ${isRTL ? 'lg:order-2' : 'lg:order-1'}`}
            >
              <div className="relative">
                {/* Main Large Image - Professional Driving Education */}
                <div className="relative rounded-2xl lg:rounded-[2rem] overflow-hidden shadow-xl">
                  <img 
                    src="/images/hero-desktop.webp" 
                    alt="Professional driving instructor teaching student" 
                    fetchPriority="high"
                    width="900"
                    height="600"
                    className="w-full h-64 sm:h-80 lg:h-[500px] object-cover"
                  />
                  
                  {/* Bottom Glass Card - Outside Image */}
                  <div className="absolute -bottom-4 start-3 end-3 sm:start-6 sm:end-6 backdrop-blur-md bg-white/95 dark:bg-slate-800/95 rounded-2xl p-4 shadow-lg border border-slate-100 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                          <StyledText translationKey="hero.confidence" />
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Text Content - Right Side (Left in RTL) */}
            <div 
              className={`order-1 lg:order-2 ${isRTL ? 'lg:order-1' : 'lg:order-2'} space-y-3 sm:space-y-6 lg:space-y-8 animate-fade-in-right`}
            >
              {/* Trust Badge */}
              <div
                className="flex flex-wrap items-center gap-3 animate-fade-in-up"
              >
                {/* B License Badge — links to the license chooser so users
                    can pick B or Taxi from a single screen. */}
                <Link
                  to="/teoriprov"
                  data-testid="hero-b-badge"
                  className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 px-3.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full text-sm font-bold border border-blue-200 dark:border-blue-800 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
                >
                  <span className="text-base leading-none">🚗</span>
                  B-körkort
                </Link>

                {/* Taxi License Badge — also routed to the license chooser
                    per product preference (was: /taxikort SEO landing). */}
                <Link
                  to="/teoriprov"
                  data-testid="hero-taxi-badge"
                  className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 px-3.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full text-sm font-bold border border-amber-200 dark:border-amber-800 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
                >
                  <span className="text-base leading-none">🚕</span>
                  Taxikort
                </Link>

                {/* Language Badge - Hidden on mobile since flags are visible.
                    Clickable → jumps to the languages section on this page. */}
                <a
                  href="#languages"
                  data-testid="hero-languages-badge"
                  className="hidden sm:inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 px-4 py-2.5 rounded-full text-sm font-bold border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors cursor-pointer"
                >
                  <Globe className="w-4 h-4" />
                  {t('hero.languageBadge')}
                </a>
              </div>
              
              {/* Main Heading */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black leading-[1.15] text-slate-900 dark:text-slate-100 md:whitespace-nowrap" data-testid="hero-title">
                <T k="hero.title" />
              </h1>
              
              {/* Subtitle */}
              <p className="text-[15px] leading-snug sm:text-xl sm:leading-relaxed max-w-xl text-slate-600 dark:text-slate-400">
                <T k="hero.subtitle" />
              </p>

              {/* أعلام اللغات المدمجة */}
              <div className="flex gap-1.5 overflow-x-auto pb-1 pt-0.5 -mx-1 px-1 scrollbar-hide">
                {[
                  { code: 'sv', flag: 'se', name: 'Svenska' },
                  { code: 'en', flag: 'gb', name: 'English' },
                  { code: 'ar', flag: 'sa', name: 'العربية' },
                  { code: 'tr', flag: 'tr', name: 'Türkçe' },
                  { code: 'fa', flag: 'ir', name: 'فارسی' },
                  { code: 'so', flag: 'so', name: 'Soomaali' },
                  { code: 'pl', flag: 'pl', name: 'Polski' },
                  { code: 'es', flag: 'es', name: 'Español' },
                  { code: 'de', flag: 'de', name: 'Deutsch' },
                ].map((lang) => (
                  <button 
                    key={lang.code}
                    onClick={() => {
                      localStorage.setItem('i18nextLng', lang.code);
                      const newPath = switchLanguagePath(window.location.pathname, lang.code);
                      window.location.href = newPath;
                    }}
                    className={`flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center border-2 transition-all active:scale-90 overflow-hidden ${
                      i18n.language === lang.code 
                        ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 shadow-sm' 
                        : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800'
                    }`}
                    data-testid={`lang-flag-${lang.code}`}
                    aria-label={`Switch to ${lang.name}`}
                  >
                    <img src={`https://flagcdn.com/w40/${lang.flag}.png`} srcSet={`https://flagcdn.com/w80/${lang.flag}.png 2x`} alt={lang.name} width="28" height="21" className="w-6 h-4 sm:w-7 sm:h-5 object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
              
              {/* CTA Buttons */}
              <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] sm:flex sm:flex-row gap-2 sm:gap-4 pt-1 sm:pt-4">
                <Button 
                  size="lg"
                  onClick={() => navigate(routes.tests)}
                  className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white min-w-0 w-full sm:w-auto whitespace-normal leading-tight h-12 sm:h-auto px-3 sm:px-10 py-0 sm:py-7 text-sm sm:text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
                  data-testid="hero-cta"
                >
                  <T k="hero.startTrial" />
                  <ArrowRight className={`w-5 h-5 ms-2 ${isRTL ? 'rotate-180' : ''}`} />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate(routes.pricing)}
                  className="rounded-full border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 min-w-0 w-full sm:w-auto whitespace-normal leading-tight h-12 sm:h-auto px-3 sm:px-10 py-0 sm:py-7 text-sm sm:text-lg font-semibold active:scale-95"
                >
                  <T k="hero.learnMore" />
                </Button>
              </div>

              {/* Stats Row - Clean Professional Style */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 pt-8">
                {stats.length > 0 ? stats.map((stat, idx) => (
                  <div
                    key={stat.id || idx}
                    className="text-center animate-fade-in-up"
                    style={{ animationDelay: `${0.5 + idx * 0.1}s` }}
                  >
                    <div className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
                      {stat.label?.[i18n.language] || stat.label?.en || ''}
                    </div>
                  </div>
                )) : (
                  <div className="col-span-4 text-center text-slate-400">Loading...</div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-white dark:bg-slate-950" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <div className="text-center mb-8 px-4 sm:px-0">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-sm font-medium mb-4">
                {t('features.whyChooseUs')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                <T k="features.title" />
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8 px-4 sm:px-0">
            {features.length > 0 ? features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              const featureTitle = feature.title?.[i18n.language] || feature.title?.en || '';
              const featureDesc = feature.description?.[i18n.language] || feature.description?.en || '';
              
              // Map feature to navigation link using localized routes
              const featureLinks = {
                'BookOpen': routes.lessons,
                'FileQuestion': routes.tests,
                'PlayCircle': routes.videos,
                'MessageCircle': routes.assistant,
                'AlertTriangle': routes.trafficSigns,
              };
              const featureLink = getLink(feature.link) || featureLinks[FeatureIcon?.name] || routes.dashboard;
              
              return (
                <div
                  key={feature.id || index}
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate(featureLink);
                    } else {
                      sessionStorage.setItem('redirectAfterLogin', featureLink);
                      navigate(routes.login);
                    }
                  }}
                  className="group relative p-5 sm:p-8 bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl border border-slate-100 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-600 hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-300 active:scale-95 overflow-hidden cursor-pointer"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-100/30 to-transparent rounded-full -mr-12 -mt-12 sm:opacity-50"></div>
                  <div className="relative z-10">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-emerald-500/30">
                      <FeatureIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-base sm:text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2 sm:mb-3 leading-tight line-clamp-2">{featureTitle}</h3>
                    <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3">{featureDesc}</p>
                    <div className="mt-4 flex items-center text-emerald-700 dark:text-emerald-400 font-medium text-sm group-hover:translate-x-1 transition-transform">
                      {t('features.explore')}
                      <ArrowRight className={`w-4 h-4 ${i18n.language === 'ar' || i18n.language === 'fa' ? 'mr-1 rotate-180' : 'ml-1'}`} />
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="col-span-4 text-center text-slate-400 py-8">Loading features...</div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-white dark:bg-slate-950 perf-below-fold" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <div className="text-center mb-8 px-4 sm:px-0">
            <span className="inline-block px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-sm font-medium mb-4">
              {t('ui.prices')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-4">
              <T k="pricing.title" />
            </h2>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto"><T k="pricing.subtitle" /></p>
          </div>


          {/* TEMP: License-type toggle (visual preview only) */}
          <div className="flex justify-center mb-4 sm:mb-6 px-4">
            <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-2xl p-1.5 shadow-inner">
              <button
                type="button"
                onClick={() => setLicenseTab('b_korkort')}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-bold transition-all ${
                  licenseTab === 'b_korkort'
                    ? 'bg-white dark:bg-slate-950 text-emerald-600 shadow-md'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                }`}
                data-testid="landing-pricing-tab-b"
              >
                🚗 B-körkort
              </button>
              <button
                type="button"
                onClick={() => setLicenseTab('taxi')}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-bold transition-all ${
                  licenseTab === 'taxi'
                    ? 'bg-white dark:bg-slate-950 text-amber-600 shadow-md'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                }`}
                data-testid="landing-pricing-tab-taxi"
              >
                🚕 Taxikort
              </button>
            </div>
          </div>
          {licenseTab === 'taxi' ? (
            <div className="text-center mb-4 -mt-1 space-y-1.5">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold">
                  ✨ {i18n.language === 'ar' ? 'اشتراك مستقل — يفتح فقط بطاقات Taxi' : i18n.language === 'sv' ? 'Separat prenumeration — låser upp endast Taxi-prov' : 'Separate subscription — unlocks Taxi tests only'}
                </span>
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-[11px] sm:text-xs font-semibold">
                  🇸🇪 {i18n.language === 'ar' ? 'محتوى Taxi متوفر بالسويدية فقط'
                        : i18n.language === 'sv' ? 'Taxi-innehåll finns endast på svenska'
                        : 'Taxi content is available in Swedish only'}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center mb-4 -mt-1">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">
                ✨ {i18n.language === 'ar' ? 'اشتراك مستقل — يفتح فقط بطاقات B-körkort' : i18n.language === 'sv' ? 'Separat prenumeration — låser upp endast B-körkort-prov' : 'Separate subscription — unlocks B-körkort tests only'}
              </span>
            </div>
          )}

          {loadingPlans ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-0 sm:gap-6 max-w-6xl mx-auto auto-rows-fr">
              {plans.map((plan, index) => {
                const lang = i18n.language;
                const isFree = plan.price === 0;
                const isTaxi = licenseTab === 'taxi';

                // Fallback bullets so Taxi tab still renders bullets on old
                // prod DBs that don't yet carry the taxiFeatures* fields.
                const TAXI_FALLBACK = {
                  sv: ['34 Taxi-övningsprov', '480+ taxifrågor', 'Säkerhet & beteende + Lagstiftning', 'AI-körlärare dygnet runt', 'Full tillgång'],
                  ar: ['34 اختبار تاكسي', '480+ سؤال تاكسي', 'السلامة والسلوك + التشريعات', 'مدرب ذكي على مدار الساعة', 'وصول كامل'],
                  en: ['34 Taxi practice tests', '480+ taxi questions', 'Safety & Behavior + Legislation', 'AI instructor 24/7', 'Full access'],
                };
                const featuresMap = isTaxi ? {
                  ar: plan.taxiFeaturesAr, sv: plan.taxiFeaturesSv, en: plan.taxiFeaturesEn,
                  tr: plan.taxiFeaturesTr, fa: plan.taxiFeaturesFa, so: plan.taxiFeaturesSo,
                  pl: plan.taxiFeaturesPl, es: plan.taxiFeaturesEs, de: plan.taxiFeaturesDe,
                } : {
                  ar: plan.featuresAr, sv: plan.featuresSv, en: plan.featuresEn,
                  tr: plan.featuresTr, fa: plan.featuresFa, so: plan.featuresSo,
                  pl: plan.featuresPl, es: plan.featuresEs, de: plan.featuresDe
                };
                const features = fixPriceNumbers(
                  featuresMap[lang]
                  || (isTaxi ? plan.taxiFeaturesEn : plan.featuresEn)
                  || (isTaxi ? plan.taxiFeatures : plan.features)
                  || (isTaxi ? (TAXI_FALLBACK[lang] || TAXI_FALLBACK.en) : null),
                  plan, plans
                );
                
                const nameMap = {
                  ar: plan.nameAr, sv: plan.nameSv, en: plan.nameEn,
                  tr: plan.nameTr, fa: plan.nameFa, so: plan.nameSo,
                  pl: plan.namePl, es: plan.nameEs, de: plan.nameDe
                };
                const planName = nameMap[lang] || plan.nameEn || plan.name;
                
                const isCurrentPlan = isAuthenticated && (
                  (isFree && (user?.subscription_status === 'trial' || user?.subscription_plan === 'free' || user?.subscription_plan === 'trial')) ||
                  (user?.subscription_status === 'active' && (
                    user?.subscription_plan_id === plan.id || 
                    user?.subscription_plan?.toLowerCase() === plan.name?.toLowerCase()
                  ))
                );
                
                const durationTexts = {
                  month: { ar: '/شهر', sv: '/månad', en: '/month', tr: '/ay', fa: '/ماه', so: '/bishii', pl: '/miesiąc', es: '/mes', de: '/Monat' },
                  months6: { ar: '/6 أشهر', sv: '/6 månader', en: '/6 months', tr: '/6 ay', fa: '/۶ ماه', so: '/6 bilood', pl: '/6 miesięcy', es: '/6 meses', de: '/6 Monate' },
                  year: { ar: '/سنة', sv: '/år', en: '/year', tr: '/yıl', fa: '/سال', so: '/sannad', pl: '/rok', es: '/año', de: '/Jahr' }
                };
                
                let durationText = '';
                if (plan.durationType === 'year' || plan.period === 'year') {
                  durationText = durationTexts.year[lang] || durationTexts.year.en;
                } else if (plan.durationType === 'months') {
                  if (plan.duration === 1) durationText = durationTexts.month[lang] || durationTexts.month.en;
                  else if (plan.duration === 6) durationText = durationTexts.months6[lang] || durationTexts.months6.en;
                  else durationText = `/${monthsLabel(plan.duration, lang)}`;
                }

                let displayPlanName = planName;
                if (plan.duration === 6 && plan.durationType === 'months') {
                  const sixMonthNames = { ar: '6 أشهر', sv: '6 månader', en: '6 Months', tr: '6 Ay', fa: '۶ ماه', so: '6 Bilood', pl: '6 miesięcy', es: '6 Meses', de: '6 Monate' };
                  displayPlanName = sixMonthNames[lang] || sixMonthNames.en;
                }

                return (
                  <PricingCard
                    key={plan.id}
                    plan={plan}
                    index={index}
                    totalPlans={plans.length}
                    features={features}
                    planName={displayPlanName}
                    durationText={durationText}
                    onSubscribe={handleSubscribe}
                    loading={null}
                    userSubscriptionStatus={user?.subscription_status}
                    isCurrentPlan={isCurrentPlan}
                    base={monthlyBase(plans)}
                    i18n={i18n}
                    t={t}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      <ReviewsSection onSeeAll={() => navigate('/recensioner')} />

      {/* CTA Section */}
      <section className="py-12 bg-slate-900 relative overflow-hidden perf-below-fold">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 start-0 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 end-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('cta.startJourney')}
          </h2>
          <p className="text-base sm:text-lg text-slate-200 mb-8 max-w-2xl mx-auto">
            {t('cta.joinStudents')}
          </p>
          <Button 
            size="lg"
            onClick={() => navigate(routes.tests)}
            className="rounded-full bg-emerald-700 hover:bg-emerald-600 text-white px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-semibold"
          >
            <T k="hero.startTrial" />
            <ArrowRight className={`w-5 h-5 ms-2 ${isRTL ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;